/**
 * DiscoverySystem.js
 *
 * Batch B (June 2026) — Surface Discoverables.
 *
 * Spawns a deterministic mix of interactive points-of-interest across a
 * planet/moon surface (seeded from the body name, like AnomalySystem):
 *   - escape_pod : a crashed survival pod (salvage → credits + AI survivor log)
 *   - debris     : fallen orbital wreckage (scavenge → scrap / ship module)
 *   - fossil     : megafauna skeleton (scan → Xeno-Bestiary entry + sellable)
 *
 * The player walks within CLAIM_RADIUS and presses E; Explore3D POSTs to
 * /api/discovery/claim, then calls markClaimed(id) here. Claimed ids persist
 * across sessions (setClaimed from the server on body load).
 *
 * Public API mirrors AnomalySystem:
 *   setBody(body), setClaimed(ids), markClaimed(id), getNearDiscovery(),
 *   getRadarPois(), tick(dt), dispose()
 */

import {
  Vector3,
  Color3,
  MeshBuilder,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

const CLAIM_RADIUS = 5.5;
const RING_MIN = 45;
const RING_MAX = 240;

const SPEC = {
  escape_pod: { count: 2, label: 'Escape Pod', verb: 'Salvage' },
  debris: { count: 3, label: 'Orbital Debris', verb: 'Scavenge' },
  fossil: { count: 2, label: 'Megafauna Fossil', verb: 'Scan' },
};

function mkRng(seed) {
  let s = (seed | 0) >>> 0;
  if (!s) s = 0xc0ffee;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function hashStr(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export default class DiscoverySystem {
  constructor(scene, opts = {}) {
    this.scene = scene;
    this.getPlayerPos = opts.getPlayerPos || (() => new Vector3(0, 0, 0));
    this.getTerrainHeight = opts.getTerrainHeight || (() => 0);
    this._root = new TransformNode('discoveryRoot', scene);
    this._items = [];   // [{ id, kind, label, verb, pos, root, mats:[], claimed }]
    this._claimed = new Set();
    this._t = 0;
  }

  setBody(body) {
    this.clear();
    if (!body) return;
    if (body.type === 'Gas Giant') return;
    const key = `${body.coords || 'k'}:${body.currentPlanetIdx ?? 0}:${body.currentMoonIdx ?? 'x'}`;
    const seed = hashStr(`${body.name || ''}|${body.type || ''}|disc`);
    const rng = mkRng(seed);
    let idx = 0;
    for (const kind of Object.keys(SPEC)) {
      const spec = SPEC[kind];
      for (let i = 0; i < spec.count; i++) {
        const ang = rng() * Math.PI * 2;
        const r = RING_MIN + rng() * (RING_MAX - RING_MIN);
        const x = Math.cos(ang) * r;
        const z = Math.sin(ang) * r;
        const y = this.getTerrainHeight(x, z);
        const id = `${key}#${kind}${i}`;
        const { root, mats } = this._buildMesh(kind, idx, rng);
        root.position.set(x, y, z);
        this._items.push({
          id, kind, label: spec.label, verb: spec.verb,
          pos: new Vector3(x, y, z), root, mats, claimed: false,
        });
        idx++;
      }
    }
  }

  _buildMesh(kind, idx, rng) {
    const root = new TransformNode(`disc_${idx}`, this.scene);
    root.parent = this._root;
    const mats = [];
    if (kind === 'escape_pod') {
      const pod = MeshBuilder.CreateCapsule(`pod_${idx}`, { radius: 1.0, height: 2.8 }, this.scene);
      pod.rotation.z = Math.PI / 2;
      pod.rotation.y = rng() * 3;
      pod.position.y = 0.95;
      const m = new StandardMaterial(`podMat_${idx}`, this.scene);
      m.diffuseColor = new Color3(0.72, 0.74, 0.8);
      m.specularColor = new Color3(0.35, 0.35, 0.4);
      pod.material = m; pod.parent = root; pod.isPickable = false; mats.push(m);
      const win = MeshBuilder.CreateDisc(`podWin_${idx}`, { radius: 0.42 }, this.scene);
      win.position.set(0, 1.05, 1.35);
      const wm = new StandardMaterial(`podWinMat_${idx}`, this.scene);
      wm.disableLighting = true; wm.emissiveColor = new Color3(0.3, 0.9, 1.0);
      win.material = wm; win.parent = root; win.isPickable = false; mats.push(wm);
    } else if (kind === 'debris') {
      const chunk = MeshBuilder.CreatePolyhedron(`deb_${idx}`, { type: idx % 4, size: 1.7 + rng() * 1.3 }, this.scene);
      chunk.position.y = 1.0;
      chunk.rotation.set(rng() * 3, rng() * 3, rng() * 3);
      const m = new StandardMaterial(`debMat_${idx}`, this.scene);
      m.diffuseColor = new Color3(0.22, 0.2, 0.2);
      m.specularColor = new Color3(0.5, 0.5, 0.55);
      m.emissiveColor = new Color3(0.14, 0.05, 0.02);
      chunk.material = m; chunk.parent = root; chunk.isPickable = false; mats.push(m);
    } else { // fossil
      const m = new StandardMaterial(`fosMat_${idx}`, this.scene);
      m.diffuseColor = new Color3(0.82, 0.78, 0.68);
      m.specularColor = new Color3(0.05, 0.05, 0.05);
      mats.push(m);
      for (let r = 0; r < 4; r++) {
        const rib = MeshBuilder.CreateTorus(`fosRib_${idx}_${r}`, {
          diameter: 5 - r * 0.6, thickness: 0.32, tessellation: 12,
        }, this.scene);
        rib.position.set(r * 1.4 - 2, 1.4, 0);
        rib.rotation.z = Math.PI / 2;
        rib.scaling.y = 0.5;
        rib.material = m; rib.parent = root; rib.isPickable = false;
      }
      const skull = MeshBuilder.CreateSphere(`fosSkull_${idx}`, {
        diameterX: 1.6, diameterY: 1.2, diameterZ: 2.2, segments: 8,
      }, this.scene);
      skull.position.set(-3.6, 1.2, 0);
      skull.material = m; skull.parent = root; skull.isPickable = false;
    }
    return { root, mats };
  }

  clear() {
    for (const it of this._items) {
      try { it.mats.forEach((m) => m.dispose()); } catch (_e) { /* noop */ }
      try { it.root.dispose(); } catch (_e) { /* noop */ }
    }
    this._items = [];
    this._claimed = new Set();
  }

  setClaimed(ids) {
    this._claimed = new Set(ids || []);
    for (const it of this._items) {
      if (this._claimed.has(it.id)) this._markVisualClaimed(it);
    }
  }

  markClaimed(id) {
    this._claimed.add(id);
    const it = this._items.find((x) => x.id === id);
    if (it) this._markVisualClaimed(it);
  }

  _markVisualClaimed(it) {
    it.claimed = true;
    it.mats.forEach((m) => {
      try { m.alpha = 0.4; m.emissiveColor = new Color3(0.1, 0.12, 0.12); } catch (_e) { /* noop */ }
    });
  }

  getNearDiscovery() {
    const p = this.getPlayerPos();
    let best = null;
    let bd = CLAIM_RADIUS;
    for (const it of this._items) {
      if (it.claimed) continue;
      const d = Math.hypot(p.x - it.pos.x, p.z - it.pos.z);
      if (d < bd) {
        bd = d;
        best = { id: it.id, kind: it.kind, label: it.label, verb: it.verb, distance: d };
      }
    }
    return best;
  }

  getRadarPois() {
    return this._items
      .filter((it) => !it.claimed)
      .map((it) => ({ id: it.id, kind: 'discovery', x: it.pos.x, z: it.pos.z }));
  }

  tick(dt) {
    this._t += dt;
    for (let i = 0; i < this._items.length; i++) {
      const it = this._items[i];
      if (!it.root || it.claimed) continue;
      if (it.kind !== 'fossil') {
        it.root.position.y = it.pos.y + Math.sin(this._t * 1.4 + i) * 0.12;
      }
    }
  }

  dispose() {
    this.clear();
    try { this._root.dispose(); } catch (_e) { /* noop */ }
  }
}
